<template>
  <div v-if="summaries.length" class="cover-automation-panel space-y-3">
    <div>
      <h4 class="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
        {{ tt('Cover External Control', 'Zunanje krmiljenje senčil') }}
      </h4>
      <p class="text-xs text-gray-500 dark:text-gray-400">
        {{ tt('Physical switch control for the cover', 'Krmiljenje senčila s fizičnim stikalom') }}
      </p>
    </div>

    <article
      v-for="automation in summaries"
      :key="automation.id"
      class="rounded-3xl border border-green-200 bg-green-50/40 p-4 shadow-sm dark:border-green-900/50 dark:bg-green-500/5">
      <!-- Header: status badge + name/status + last action -->
      <div class="flex items-center gap-3">
        <div
          class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-colors"
          :class="
            display(automation).lastAction && display(automation).lastAction !== 'stop'
              ? 'bg-green-500/20 text-green-500'
              : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
          ">
          <ControlIcon name="toggle" size="1.25rem" />
        </div>

        <div class="min-w-0 flex-1">
          <h5 class="truncate text-base font-semibold text-gray-900 dark:text-white">
            {{ automation.name }}
          </h5>
          <p class="truncate text-sm text-gray-600 dark:text-gray-300">
            {{ statusLabel(automation) }}
          </p>
        </div>

        <div class="shrink-0 rounded-2xl bg-green-100/70 px-3 py-2 text-right dark:bg-green-500/10">
          <div class="text-lg font-semibold leading-none text-green-600 dark:text-green-300">
            {{ actionLabel(automation) }}
          </div>
          <div class="mt-1 text-[11px] uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
            {{ tt('Last action', 'Zadnja akcija') }}
          </div>
        </div>
      </div>

      <!-- Detail line -->
      <div class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
        <span>{{ tt('Mode:', 'Način:') }} {{ modeLabel(automation) }}</span>
        <span v-if="display(automation).lastPosition != null">
          {{ tt('Last position:', 'Zadnji položaj:') }} {{ display(automation).lastPosition }}%
        </span>
        <span
          v-if="automation.enabled === false"
          class="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400">
          {{ tt('Disabled', 'Onemogočeno') }}
        </span>
      </div>

      <!-- Full settings — CONFIGURATION, so the editor and its Save button are
           the admin's alone (cards t_bd611e14 / t_a4368691; D12: settings
           hidden, control shown). A non-admin keeps the status display above. -->
      <template v-if="isAdmin">
        <div class="mt-3 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            class="flex items-center gap-1 text-xs font-semibold text-green-600 hover:text-green-700 dark:text-green-400"
            @click="toggleExpanded(automation.id)">
            <ControlIcon :name="isExpanded(automation.id) ? 'chevron-up' : 'chevron-down'" size="0.875rem" />
            {{
              isExpanded(automation.id)
                ? tt('Hide full settings', 'Skrij vse nastavitve')
                : tt('Edit full settings', 'Uredi vse nastavitve')
            }}
          </button>
        </div>

        <div
          v-if="isExpanded(automation.id)"
          class="mt-2 rounded-2xl border border-gray-200 bg-white/80 p-3 dark:border-gray-700 dark:bg-gray-900/60">
          <CoverExternalControlEditor
            :model-value="draftConfig(automation.id)"
            @update:model-value="setDraftConfig(automation.id, $event)" />
        </div>

        <!-- Save -->
        <div class="mt-4 flex justify-end">
          <button
            type="button"
            class="rounded-full px-4 py-2 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60"
            :class="isDirty(automation) ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-300 dark:bg-gray-700'"
            :disabled="!isDirty(automation) || isSaving(automation.id) || hasErrors(automation)"
            @click="saveAutomation(automation)">
            {{ isSaving(automation.id) ? tt('Saving…', 'Shranjujem…') : tt('Save changes', 'Shrani spremembe') }}
          </button>
        </div>
      </template>
    </article>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { apiFetch } from '../../../services/api';
import { useAuthStore } from '../../../stores/auth';
import { useDeviceAutomationsStore } from '../../../stores/deviceAutomations';
import { useDeviceValues } from '../../../composables/useDeviceValues';
import {
  COVER_ACTION_LABELS,
  COVER_EXTERNAL_CONTROL_TYPE,
  COVER_SWITCH_MODE_LABELS,
  buildCoverExternalControlDisplay,
  getCoverExternalControlErrors,
  normalizeCoverExternalControlConfig,
} from '../../../utils/coverAutomation';
import type { DeviceAutomationSummary } from '../../../stores/deviceAutomations';
import CoverExternalControlEditor from './CoverExternalControlEditor.vue';
import ControlIcon from '../../device-icons/ControlIcon.vue';
import { tt } from '../../../utils/consoleText';
import type { HubConfigBag } from '../deviceWidgetTypes';

const props = defineProps<{
  deviceId: string;
}>();

const emit = defineEmits<{
  updated: [];
}>();

const store = useDeviceAutomationsStore();
const authStore = useAuthStore();
const { currentValues } = useDeviceValues(props.deviceId);

/**
 * Editing an automation is *configuration* of the house (card `t_bd611e14`):
 * the hub serves every device-automation mutation `authenticate +
 * authorize("admin")`, so the editor and its Save button belong to an admin
 * (card `t_a4368691`; D12 — an automation schedule is configuration, so the
 * settings are not offered below that). The status display above stays: the
 * reads are `authenticate`, kept for exactly these device sheets.
 */
const isAdmin = computed(() => authStore.isAdmin);

const draftConfigs = reactive<Record<string, HubConfigBag>>({});
const configSignatures = reactive<Record<string, string>>({});
const savingIds = ref<string[]>([]);
const expandedIds = ref<string[]>([]);

const summaries = computed<DeviceAutomationSummary[]>(() =>
  store
    .getDeviceAutomationSummaries(props.deviceId)
    .filter((automation) => automation.automationType === COVER_EXTERNAL_CONTROL_TYPE),
);

watch(
  summaries,
  (list) => {
    for (const automation of list) {
      const normalized = normalizeCoverExternalControlConfig(automation.config);
      const signature = JSON.stringify(normalized);
      if (configSignatures[automation.id] !== signature) {
        draftConfigs[automation.id] = JSON.parse(signature);
        configSignatures[automation.id] = signature;
      }
    }
  },
  { immediate: true },
);

onMounted(() => {
  void store.fetchDeviceAutomationSummaries(props.deviceId);
});

function draftConfig(automationId: string): HubConfigBag {
  if (!draftConfigs[automationId]) draftConfigs[automationId] = {};
  return draftConfigs[automationId];
}

function setDraftConfig(automationId: string, next: HubConfigBag) {
  draftConfigs[automationId] = JSON.parse(JSON.stringify(next));
}

function isExpanded(automationId: string): boolean {
  return expandedIds.value.includes(automationId);
}

function toggleExpanded(automationId: string) {
  expandedIds.value = isExpanded(automationId)
    ? expandedIds.value.filter((id) => id !== automationId)
    : [...expandedIds.value, automationId];
}

function display(automation: DeviceAutomationSummary) {
  return buildCoverExternalControlDisplay(automation, currentValues.value);
}

function statusLabel(automation: DeviceAutomationSummary): string {
  if (automation.enabled === false) return tt('Automation disabled', 'Avtomatizacija onemogočena');
  return tt('Waiting for switch', 'Čakanje na stikalo');
}

function actionLabel(automation: DeviceAutomationSummary): string {
  const lastAction = display(automation).lastAction;
  if (!lastAction) return '—';
  const label = COVER_ACTION_LABELS[lastAction];
  return label ? tt(label.en, label.sl) : lastAction.replace(/_/g, ' ');
}

function modeLabel(automation: DeviceAutomationSummary): string {
  const cfg = normalizeCoverExternalControlConfig(automation.config);
  const label = COVER_SWITCH_MODE_LABELS[cfg.switch_mode];
  return label ? tt(label.en, label.sl) : cfg.switch_mode;
}

function isDirty(automation: DeviceAutomationSummary): boolean {
  return JSON.stringify(draftConfig(automation.id)) !== (configSignatures[automation.id] ?? '');
}

function hasErrors(automation: DeviceAutomationSummary): boolean {
  return getCoverExternalControlErrors(draftConfig(automation.id)).length > 0;
}

function isSaving(automationId: string): boolean {
  return savingIds.value.includes(automationId);
}

async function saveAutomation(automation: DeviceAutomationSummary) {
  if (hasErrors(automation)) return;
  const automationId = automation.id;
  savingIds.value = [...savingIds.value, automationId];
  try {
    await apiFetch(`/device-automations/${automationId}`, {
      method: 'PUT',
      body: JSON.stringify({ config: draftConfig(automationId) }),
    });
    emit('updated');
    await refresh();
  } catch (error) {
    console.error(`[CoverAutomationPanel] Failed to save automation ${automationId}`, error);
  } finally {
    savingIds.value = savingIds.value.filter((id) => id !== automationId);
  }
}

async function refresh() {
  store.invalidate(props.deviceId);
  await Promise.all([
    store.fetchDeviceAutomationSummaries(props.deviceId, true),
    store.fetchDeviceAutomations(props.deviceId, true),
  ]);
}
</script>
